import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { Bell, Plus, Trash2, Clock, Loader2 } from 'lucide-react';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

const RemindersPage = () => {
    const { api } = useAuth();
    const [reminders, setReminders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [newReminder, setNewReminder] = useState({
        time: '09:00',
        days: ['monday', 'wednesday', 'friday'],
        message: 'Time to write in your journal!'
    });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchReminders();
    }, []);

    const fetchReminders = async () => {
        try {
            const response = await api.get('/reminders/');
            setReminders(response.data);
        } catch (error) {
            console.error('Failed to fetch reminders:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateReminder = async () => {
        if (newReminder.days.length === 0) {
            toast.error('Please select at least one day');
            return;
        }

        setSaving(true);
        try {
            const response = await api.post('/reminders/', newReminder);
            setReminders([...reminders, response.data]);
            setShowForm(false);
            setNewReminder({
                time: '09:00',
                days: ['monday', 'wednesday', 'friday'],
                message: 'Time to write in your journal!'
            });
            toast.success('Reminder created!');
        } catch (error) {
            toast.error('Failed to create reminder');
        } finally {
            setSaving(false);
        }
    };

    const handleToggle = async (reminderId) => {
        try {
            const response = await api.put(`/reminders/${reminderId}/toggle`);
            setReminders(reminders.map(r => 
                r.id === reminderId ? { ...r, is_active: response.data.is_active } : r
            ));
            toast.success(response.data.is_active ? 'Reminder enabled' : 'Reminder paused');
        } catch (error) {
            toast.error('Failed to update reminder');
        }
    };

    const handleDelete = async (reminderId) => {
        if (!window.confirm('Delete this reminder?')) return;
        
        try {
            await api.delete(`/reminders/${reminderId}`);
            setReminders(reminders.filter(r => r.id !== reminderId));
            toast.success('Reminder deleted');
        } catch (error) {
            toast.error('Failed to delete reminder');
        }
    };

    const toggleDay = (day) => {
        setNewReminder(prev => ({
            ...prev,
            days: prev.days.includes(day)
                ? prev.days.filter(d => d !== day)
                : [...prev.days, day]
        }));
    };

    return (
        <DashboardLayout>
            <div data-testid="reminders-page" className="max-w-3xl mx-auto space-y-6">
                <div className="flex items-center justify-between">
                    <h1 className="font-heading text-3xl font-semibold text-foreground">
                        Journal Reminders
                    </h1>
                    <Button
                        onClick={() => setShowForm(!showForm)}
                        className="rounded-full"
                        data-testid="add-reminder-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Reminder
                    </Button>
                </div>

                <p className="text-muted-foreground">
                    Set up reminders to maintain your journaling habit. We'll send you email notifications at your chosen times.
                </p>

                {/* New Reminder Form */}
                {showForm && (
                    <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <Card className="glass-card">
                            <CardHeader>
                                <CardTitle className="font-heading text-xl">New Reminder</CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-6">
                                <div className="space-y-2">
                                    <Label>Time</Label>
                                    <Input
                                        type="time"
                                        value={newReminder.time}
                                        onChange={(e) => setNewReminder({ ...newReminder, time: e.target.value })}
                                        className="h-12 rounded-xl w-40"
                                        data-testid="reminder-time-input"
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label>Days</Label>
                                    <div className="flex flex-wrap gap-2">
                                        {DAYS.map(day => (
                                            <Button
                                                key={day}
                                                type="button"
                                                variant={newReminder.days.includes(day) ? "default" : "outline"}
                                                onClick={() => toggleDay(day)}
                                                className="rounded-full capitalize"
                                                size="sm"
                                                data-testid={`day-${day}`}
                                            >
                                                {day.substring(0, 3)}
                                            </Button>
                                        ))}
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Custom Message (optional)</Label>
                                    <Input
                                        value={newReminder.message}
                                        onChange={(e) => setNewReminder({ ...newReminder, message: e.target.value })}
                                        placeholder="Time to write in your journal!"
                                        className="h-12 rounded-xl"
                                        data-testid="reminder-message-input"
                                    />
                                </div>

                                <div className="flex gap-3">
                                    <Button
                                        onClick={handleCreateReminder}
                                        disabled={saving}
                                        className="rounded-full"
                                        data-testid="save-reminder-btn"
                                    >
                                        {saving ? (
                                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        ) : null}
                                        Save Reminder
                                    </Button>
                                    <Button
                                        variant="outline"
                                        onClick={() => setShowForm(false)}
                                        className="rounded-full"
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}

                {/* Reminders List */}
                {loading ? (
                    <div className="text-center py-12">
                        <div className="animate-pulse text-muted-foreground">Loading reminders...</div>
                    </div>
                ) : reminders.length === 0 ? (
                    <Card className="glass-card">
                        <CardContent className="p-12 text-center">
                            <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                            <p className="text-muted-foreground">
                                No reminders set. Create one to stay consistent with your journaling!
                            </p>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {reminders.map((reminder) => (
                            <motion.div
                                key={reminder.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                            >
                                <Card className={`glass-card ${!reminder.is_active ? 'opacity-60' : ''}`}>
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between gap-4">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
                                                    <Clock className="w-6 h-6 text-primary" />
                                                </div>
                                                <div>
                                                    <p className="font-heading text-xl font-semibold">
                                                        {reminder.time}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground">
                                                        {reminder.days.map(d => d.substring(0, 3)).join(', ')}
                                                    </p>
                                                    <p className="text-sm text-muted-foreground mt-1">
                                                        {reminder.message}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <Switch
                                                    checked={reminder.is_active}
                                                    onCheckedChange={() => handleToggle(reminder.id)}
                                                    data-testid={`toggle-reminder-${reminder.id}`}
                                                />
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleDelete(reminder.id)}
                                                    className="text-muted-foreground hover:text-destructive"
                                                    data-testid={`delete-reminder-${reminder.id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default RemindersPage;
